import { logger } from "@/lib/logger";
import { supabase } from "@/lib/supabase";
import type { HubCta, HubCtaTarget } from "@/types/hubCtas";

const TARGETS = new Set<HubCtaTarget>([
  "non_member",
  "regular",
  "monthly",
  "trial_beginner_piano",
]);

function isTarget(v: unknown): v is HubCtaTarget {
  return typeof v === "string" && TARGETS.has(v as HubCtaTarget);
}

function normalizeHubCta(raw: Partial<HubCta>): HubCta | null {
  const id = typeof raw.id === "number" ? raw.id : Number(raw.id);
  if (!Number.isFinite(id)) return null;
  if (!isTarget(raw.target)) return null;
  if (typeof raw.title !== "string" || !raw.title.trim()) return null;

  const priority =
    typeof raw.priority === "number" && Number.isFinite(raw.priority)
      ? raw.priority
      : Number(raw.priority);
  if (!Number.isFinite(priority)) return null;

  return {
    id,
    enabled: raw.enabled !== false,
    target: raw.target,
    priority,
    title: raw.title.trim(),
    summary: typeof raw.summary === "string" ? raw.summary.trim() : "",
    cta_label:
      typeof raw.cta_label === "string" && raw.cta_label.trim()
        ? raw.cta_label.trim()
        : "Learn more",
    link: typeof raw.link === "string" ? raw.link.trim() : "",
    image_url:
      typeof raw.image_url === "string" && raw.image_url.trim()
        ? raw.image_url.trim()
        : null,
  };
}

function rowsFromRpc(data: unknown): Partial<HubCta>[] {
  if (data == null) return [];
  if (Array.isArray(data)) {
    return data.filter((r): r is Partial<HubCta> => r != null && typeof r === "object");
  }
  if (typeof data === "object") return [data as Partial<HubCta>];
  return [];
}

/** `get_active_hub_ctas` → 0..n rows by priority ASC (disabled skipped by RPC). */
export async function fetchActiveHubCtasForTarget(
  target: HubCtaTarget,
): Promise<HubCta[]> {
  try {
    const {
      data: { session },
    } = await supabase.auth.getSession();
    if (!session) return [];

    const { data, error } = await supabase.rpc("get_active_hub_ctas", {
      p_target: target,
    });

    if (error) {
      logger.warn("hubCtas: get_active_hub_ctas failed", {
        target,
        message: error.message,
        code: error.code,
      });
      return [];
    }

    const rows = rowsFromRpc(data)
      .map(normalizeHubCta)
      .filter((r): r is HubCta => r != null);

    return rows;
  } catch (e) {
    logger.warn("hubCtas: get_active_hub_ctas exception", { target, e });
    return [];
  }
}

/** Fetch CTAs for each subscription target (target order; priority ASC within). */
export async function fetchActiveHubCtas(
  targets: HubCtaTarget[],
): Promise<HubCta[]> {
  if (!targets.length) return [];
  const batches = await Promise.all(
    targets.map((t) => fetchActiveHubCtasForTarget(t)),
  );
  return batches.flat();
}
